import React, { useState, useContext, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Col, Container, Row } from 'reactstrap';
import types from '../../../types/services-api';
import UploadPhotoModal from '../../common/UploadPhotoModal';
import agent from '../../../agent';
import {
  OfficesContext,
  OfficesContextType,
} from '../../../context/ProfileContext';
import {
  UserContext,
  UserContextType,
  isEmployerType,
} from '../../../context/UserContext';

const OfficePhotos = (props: { office: types.OfficeProfileViewType }) => {
  const { user } = useContext(UserContext) as UserContextType;
  const { offices, upsertOffice } = useContext(
    OfficesContext
  ) as OfficesContextType;
  const [errorMessage, setErrorMessage] = useState('');
  const [defaultPhoto, setDefaultPhoto] = useState('');

  useEffect(() => {
    const defaultOfficePhoto = agent.Employer.defaultOfficePhoto();
    setDefaultPhoto(defaultOfficePhoto);
  });

  function addOfficePhoto(photoFile: Blob) {
    const officeProps = {
      officeId: props.office.id,
      description: '',
      photoFile,
    };
    agent.Employer.uploadOfficePhoto({ ...officeProps })
      .then((res) => {
        let updatedOffice = props.office;
        updatedOffice.images = [...props.office.images, res.data];
        upsertOffice(updatedOffice);
      })
      .catch((err) => {
        setErrorMessage(err.message);
      });
  }

  function updateOfficePhoto(photoFile: Blob, index: number) {
    agent.Employer.editOfficePhoto({
      signedPhotoUrl: props.office.images[index].imageUrl,
      description: props.office.images[index].description,
      photoFile,
    })
      .then((res) => {
        let updatedOffice = props.office;
        updatedOffice.images[index].imageUrl = res.data.imageUrl;
        updatedOffice.images[index].imageThumbnailUrl =
          res.data.imageThumbnailUrl;
        updatedOffice.images[index].imageFullUrl = res.data.imageFullUrl;
        upsertOffice(updatedOffice);
      })
      .catch((err) => {
        setErrorMessage(err.message);
      });
  }

  function deleteOfficePhoto(index: number) {
    agent.Employer.deleteOfficePhoto({
      officeId: props.office.id,
      photoId: props.office.images[index].id,
    })
      .then((res) => {
        let updatedOffice = props.office;

        updatedOffice.images = updatedOffice.images.filter(
          (i) => i.id !== props.office.images[index].id
        );
        upsertOffice(updatedOffice);
      })
      .catch((err) => {
        setErrorMessage(err.message);
      });
  }

  function reorder(list, startIndex, endIndex) {
    const result = list;
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  function onDragEnd(result) {
    if (!result.destination) {
      return;
    }

    // find the office
    const office = offices.find(
      (office) => 'droppable-' + office.id === result.source.droppableId
    );
    if (!!office) {
      // reorder
      const items = reorder(
        office.images,
        result.source.index,
        result.destination.index
      );

      // send to server
      let ids: { id: number }[] = [];
      items.map((office, index) => {
        ids[index] = office.id;
      });
      agent.Employer.sortOfficePhotos({ officeId: office.id, ids: ids });

      // update context
      office.images = items;
      upsertOffice(office);
    }
  }

  const getItemStyle = (isDragging: boolean, draggableStyle) => {
    return {
      ...draggableStyle,
      top: 0,
      left: 0,
    };
  };
  if (isEmployerType(user)) {
    return (
      <Container fluid>
        <Row className="rounded bg-off-white ">
          <Col xs="auto" className={`mb-4 p-0`}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId={`droppable-${props.office.id}`}
                direction="horizontal"
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${
                      snapshot.isDraggingOver ? 'bg-light' : 'bg-off-white'
                    } rounded d-flex flex-wrap`}
                  >
                    {props.office.images.length > 0 &&
                      props.office.images.map((photo, index) => (
                        <Draggable
                          draggableId={`draggable-${photo.id}`}
                          index={index}
                          key={`draggable-${photo.id}`}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                              // key={photo.id}
                              className="position-relative"
                            >
                              <img
                                src={photo.imageThumbnailUrl}
                                className="img-fluid m-3"
                                alt="office photo"
                                style={{ height: '100px', width: 'auto' }}
                              />
                              <div
                                className="rounded-circle position-absolute p-1 bg-off-white"
                                style={{
                                  right: '-5%',
                                  bottom: '-5%',
                                }}
                              >
                                <UploadPhotoModal
                                  hasPhoto={true}
                                  photoUrl={photo.imageThumbnailUrl}
                                  resourceName={'Office'}
                                  onUpdate={(photoFile) =>
                                    updateOfficePhoto(photoFile, index)
                                  }
                                  onDelete={() => deleteOfficePhoto(index)}
                                  photoErrorMessage={errorMessage}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Col>
          <Col xs="auto" className="p-0 mb-4">
            {!!defaultPhoto && (
              <div className="position-relative">
                <img
                  src={defaultPhoto}
                  alt={'placeholder office image'}
                  className="img-fluid m-3"
                  style={{ height: '100px', width: 'auto' }}
                />
                <div
                  className="rounded-circle position-absolute p-1 bg-off-white"
                  style={{
                    right: '-5%',
                    bottom: '-5%',
                  }}
                >
                  <UploadPhotoModal
                    hasPhoto={false}
                    photoUrl={defaultPhoto}
                    resourceName={'Office'}
                    onAdd={addOfficePhoto}
                    photoErrorMessage={errorMessage}
                  />
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    );
  } else {
    return (
      <div className="d-flex flex-wrap">
        {props.office.images.length > 0 &&
          props.office.images.map((photo, index) => (
            <div key={`${photo.id}`} className="col-auto">
              <img
                src={photo.imageThumbnailUrl}
                className="img-fluid m-3"
                alt="office photo"
                style={{ height: '100px', width: 'auto' }}
              />
            </div>
          ))}
      </div>
    );
  }
};

export default OfficePhotos;
